import React, { useState, useEffect } from 'react';
import { Modal } from '../../design-system';
import { BUTTON_PRESETS } from '../../design-system/constants';
import { Form, Input, InputNumber, Checkbox, Select, Tooltip, message } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import ApiServiceNode from '../../service/ApiServiceNode';
import { getApiErrorMessage } from '../../utils/errorUtils';
import logger from '../../utils/logger';
import countries from '../../constants/countries';



const { TextArea } = Input;

const parseLeaveTypeAttributes = (attributes) => {
    if (!attributes) return {};
    if (typeof attributes !== 'string') return attributes;

    try {
        return JSON.parse(attributes);
    } catch (error) {
        logger.error('Error parsing attributes:', error);
        return {};
    }
};

const formatRuleValue = (value) => String(value || '')
    .replace(/^form\.[^.]+\./, '')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

const formatEligibilityRules = (attributes) => {
    const eligibility = parseLeaveTypeAttributes(attributes).eligibility;
    if (!eligibility) return ['No eligibility rules configured'];

    const rules = [];
    if (eligibility.gender) {
        rules.push(`Gender: ${formatRuleValue(eligibility.gender)}`);
    }
    if (Array.isArray(eligibility.nationality) && eligibility.nationality.length > 0) {
        rules.push(`Nationality: ${eligibility.nationality.join(', ')}`);
    }
    if (Array.isArray(eligibility.nationality_exclude) && eligibility.nationality_exclude.length > 0) {
        rules.push(`Excluded nationality: ${eligibility.nationality_exclude.join(', ')}`);
    }

    return rules.length > 0 ? rules : ['No eligibility rules configured'];
};

const getResponseData = (response) => response?.data?.data || response?.data || {};

const GENDER_ELIGIBILITY_OPTIONS = [
    { label: 'Any gender', value: '' },
    { label: 'Male only', value: 'form.gender.male' },
    { label: 'Female only', value: 'form.gender.female' },
];

const NATIONALITY_OPTIONS = countries.map((country) => ({
    label: `${country.code} - ${country.name}`,
    value: country.code
}));

const policyTopRowStyle = {
    display: 'flex',
    flexWrap: 'wrap',
    columnGap: 16,
    rowGap: 0,
    alignItems: 'flex-start'
};

const compactDaysFieldStyle = {
    flex: '0 0 92px'
};

const inlineCheckboxFieldStyle = {
    flex: '0 0 auto',
    paddingTop: 30
};

const eligibilitySelectStyle = {
    minHeight: 46
};

const daysLabel = (
    <span>
        Days{' '}
        <Tooltip title="Number of days for this leave type">
            <InfoCircleOutlined style={{ color: '#8c8c8c', fontSize: 12 }} />
        </Tooltip>
    </span>
);

const formGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    columnGap: 16,
    rowGap: 0,
    alignItems: 'start'
};

const fullWidthFieldStyle = {
    gridColumn: '1 / -1'
};

const formSectionStyle = {
    margin: 0
};

const dividedFormSectionStyle = {
    borderTop: '1px solid #f0f0f0',
    marginTop: 8,
    paddingTop: 20
};

const formSectionTitleStyle = {
    color: '#262626',
    fontSize: 14,
    fontWeight: 600,
    lineHeight: 1.4,
    margin: '0 0 14px'
};

const formSectionDescriptionStyle = {
    color: '#737373',
    fontSize: 13,
    lineHeight: 1.5,
    margin: '-6px 0 16px'
};

const normalizeCodeList = (value) => {
    if (!Array.isArray(value)) return [];
    return [...new Set(value
        .map((item) => String(item || '').trim().toUpperCase())
        .filter(Boolean))];
};

const getExistingEligibility = (attributes) => {
    const parsedAttributes = parseLeaveTypeAttributes(attributes);
    const eligibility = parsedAttributes.eligibility;
    return eligibility && typeof eligibility === 'object' && !Array.isArray(eligibility)
        ? eligibility
        : {};
};

const buildEligibility = (existingAttributes, values = {}) => {
    const existingEligibility = getExistingEligibility(existingAttributes);
    const nextEligibility = { ...existingEligibility };
    const nationality = normalizeCodeList(values.nationality);
    const nationalityExclude = normalizeCodeList(values.nationalityExclude);

    if (values.gender) {
        nextEligibility.gender = values.gender;
    } else {
        delete nextEligibility.gender;
    }

    if (nationality.length > 0) {
        nextEligibility.nationality = nationality;
    } else {
        delete nextEligibility.nationality;
    }

    if (nationalityExclude.length > 0) {
        nextEligibility.nationality_exclude = nationalityExclude;
    } else {
        delete nextEligibility.nationality_exclude;
    }

    if (Object.keys(nextEligibility).length > 0) {
        return nextEligibility;
    }

    return parseLeaveTypeAttributes(existingAttributes).eligibility ? null : undefined;
};

const LeaveTypeFormModal = ({ visible, leaveType, officeId, onClose }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [daysChangeConfirm, setDaysChangeConfirm] = useState(null);

    const normalizeDays = (value) => {
        const numericValue = Number(value ?? 0);
        return Number.isFinite(numericValue) ? numericValue : 0;
    };

    useEffect(() => {
        if (visible) {
            if (leaveType) {
                // Parse attributes if stored as JSON
                let eligibilityGender = undefined;
                let eligibilityNationality = undefined;
                let eligibilityNationalityExclude = undefined;
                if (leaveType.attributes) {
                    const attrs = parseLeaveTypeAttributes(leaveType.attributes);
                    eligibilityGender = attrs.eligibility?.gender || undefined;
                    eligibilityNationality = attrs.eligibility?.nationality || undefined;
                    eligibilityNationalityExclude = attrs.eligibility?.nationality_exclude || undefined;
                }

                form.setFieldsValue({
                    leave_name: leaveType.leave_name,
                    description: leaveType.description,
                    days: leaveType.days,
                    is_active: leaveType.is_active,
                    eligibility_gender: eligibilityGender,
                    eligibility_nationality: eligibilityNationality,
                    eligibility_nationality_exclude: eligibilityNationalityExclude
                });
            } else {
                form.resetFields();
                // Set defaults for new leave type
                form.setFieldsValue({
                    is_active: true,
                    eligibility_gender: undefined,
                    eligibility_nationality: undefined,
                    eligibility_nationality_exclude: undefined
                });
            }
        }
    }, [visible, leaveType, form]);

    const saveLeaveType = async (leaveTypeData, options = {}) => {
        setLoading(true);
        try {
            if (leaveType) {
                await ApiServiceNode.put(`/settings/offices/leave-types/${leaveType.id}`, leaveTypeData);
                if (Number.isFinite(options.impactedEntitlements)) {
                    message.success('Leave type updated. Existing entitlements were not changed.');
                } else {
                    message.success('Leave type updated successfully');
                }
            } else {
                await ApiServiceNode.post('/settings/offices/leave-types', leaveTypeData);
                message.success('Leave type created successfully');
            }

            setDaysChangeConfirm(null);
            onClose(true); // Refresh list
        } catch (error) {
            logger.error('Error saving leave type:', error);
            message.error(getApiErrorMessage(error, 'Failed to save leave type'));
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            const selectedNationality = normalizeCodeList(values.eligibility_nationality);
            const excludedNationality = normalizeCodeList(values.eligibility_nationality_exclude);
            if (selectedNationality.length > 0 && excludedNationality.length > 0) {
                message.error('Use either allowed nationalities or excluded nationalities, not both.');
                return;
            }

            const eligibility = buildEligibility(leaveType?.attributes, {
                gender: values.eligibility_gender,
                nationality: selectedNationality,
                nationalityExclude: excludedNationality
            });
            const attributes = {
                label: values.leave_name
            };
            if (eligibility !== undefined) {
                attributes.eligibility = eligibility;
            }

            // Prepare leave type data
            const leaveTypeData = {
                leave_name: values.leave_name,
                description: values.description,
                days: values.days || 0,
                is_active: values.is_active ?? true,
                office_id: officeId,
                attributes
            };

            const previousDays = normalizeDays(leaveType?.days);
            const nextDays = normalizeDays(leaveTypeData.days);
            if (leaveType && previousDays !== nextDays) {
                setDaysChangeConfirm({
                    previousDays,
                    nextDays,
                    diffDays: nextDays - previousDays,
                    leaveTypeData,
                    impactedEntitlements: null,
                    impactLoading: true,
                    impactError: null
                });
                try {
                    const impactResponse = await ApiServiceNode.get(
                        `/settings/offices/leave-types/${leaveType.id}/entitlement-impact`
                    );
                    const impactData = getResponseData(impactResponse);
                    setDaysChangeConfirm((current) => current ? {
                        ...current,
                        impactedEntitlements: Number(impactData.impactedEntitlements ?? 0),
                        impactLoading: false,
                        impactError: null
                    } : current);
                } catch (error) {
                    logger.error('Error checking entitlement impact:', error);
                    setDaysChangeConfirm((current) => current ? {
                        ...current,
                        impactedEntitlements: null,
                        impactLoading: false,
                        impactError: 'Unable to load entitlement impact count.'
                    } : current);
                }
                return;
            }

            await saveLeaveType(leaveTypeData);
        } catch (error) {
            logger.error('Error saving leave type:', error);
            message.error(getApiErrorMessage(error, 'Failed to save leave type'));
        }
    };

    const handleConfirmDaysChange = async () => {
        if (!daysChangeConfirm?.leaveTypeData) return;
        await saveLeaveType(daysChangeConfirm.leaveTypeData, {
            impactedEntitlements: daysChangeConfirm.impactedEntitlements
        });
    };

    const currentYear = new Date().getFullYear();
    const eligibilityRules = formatEligibilityRules(daysChangeConfirm?.leaveTypeData?.attributes || leaveType?.attributes);

    return (
        <>
            <Modal visible={visible} onClose={() => onClose(false)} width="medium">
                <Modal.Header
                    title={leaveType ? 'Edit Leave Type' : 'Add Leave Type'}
                    subtitle={leaveType
                        ? 'Update this unused leave type and its current defaults'
                        : 'Create a new leave type and set its initial defaults'}
                />
                <Modal.Body padding="default">
                    <Form form={form} layout="vertical">
                        <section aria-labelledby="leave-type-details-heading" style={formSectionStyle}>
                            <h3 id="leave-type-details-heading" style={formSectionTitleStyle}>Leave type details</h3>
                            <div style={formGridStyle}>
                                <Form.Item
                                    label="Leave Type Name"
                                    name="leave_name"
                                    style={fullWidthFieldStyle}
                                    rules={[
                                        { required: true, message: 'Please enter leave type name' },
                                        { max: 255, message: 'Maximum 255 characters' }
                                    ]}
                                >
                                    <Input placeholder="e.g., Maternity Leave" maxLength={255} />
                                </Form.Item>

                                <Form.Item
                                    label="Description"
                                    name="description"
                                    style={fullWidthFieldStyle}
                                >
                                    <TextArea
                                        placeholder="Leave type description"
                                        rows={3}
                                    />
                                </Form.Item>
                            </div>
                        </section>

                        <section aria-labelledby="initial-policy-defaults-heading" style={dividedFormSectionStyle}>
                            <h3 id="initial-policy-defaults-heading" style={formSectionTitleStyle}>Initial policy defaults</h3>
                            <p style={formSectionDescriptionStyle}>
                                Used for entitlement generation until this leave type is managed through Leave Policy.
                            </p>

                            <div style={policyTopRowStyle}>
                                <Form.Item
                                    label={daysLabel}
                                    name="days"
                                    style={compactDaysFieldStyle}
                                >
                                    <InputNumber
                                        min={0}
                                        style={{ width: '100%' }}
                                        placeholder="e.g., 14"
                                    />
                                </Form.Item>

                                <Form.Item
                                    name="is_active"
                                    valuePropName="checked"
                                    style={inlineCheckboxFieldStyle}
                                >
                                    <Checkbox>Active in this office</Checkbox>
                                </Form.Item>
                            </div>

                            <div style={formGridStyle}>
                                <Form.Item
                                    label="Eligible Gender"
                                    name="eligibility_gender"
                                >
                                    <Select
                                        placeholder="Any gender"
                                        style={eligibilitySelectStyle}
                                        options={GENDER_ELIGIBILITY_OPTIONS}
                                    />
                                </Form.Item>

                                <Form.Item
                                    label="Allowed Nationalities"
                                    name="eligibility_nationality"
                                >
                                    <Select
                                        mode="multiple"
                                        allowClear
                                        showSearch
                                        optionFilterProp="label"
                                        placeholder="Any nationality"
                                        options={NATIONALITY_OPTIONS}
                                    />
                                </Form.Item>

                                <Form.Item
                                    label="Excluded Nationalities"
                                    name="eligibility_nationality_exclude"
                                >
                                    <Select
                                        mode="multiple"
                                        allowClear
                                        showSearch
                                        optionFilterProp="label"
                                        placeholder="No exclusions"
                                        options={NATIONALITY_OPTIONS}
                                    />
                                </Form.Item>
                            </div>
                        </section>
                    </Form>
                </Modal.Body>
                <Modal.Footer buttons={[
                    {
                        ...BUTTON_PRESETS.SAVE,
                        key: leaveType ? 'update-leave-type' : 'create-leave-type',
                        label: leaveType ? 'Update' : 'Create Leave Type',
                        loading,
                        onClick: handleSave
                    }
                ]} />
            </Modal>

            <Modal
                visible={Boolean(daysChangeConfirm)}
                onClose={() => {
                    if (!loading) {
                        setDaysChangeConfirm(null);
                    }
                }}
                width="small"
            >
                <Modal.Header
                    title="Confirm Days Change"
                    alert={{
                        type: 'warning',
                        message: 'This updates the leave policy only. Existing entitlements will not be changed automatically.'
                    }}
                />
                <Modal.Body padding="default">
                    {daysChangeConfirm && (
                        <>
                            <p style={{ margin: 0, lineHeight: 1.6 }}>
                                Changing Days from {daysChangeConfirm.previousDays} to {daysChangeConfirm.nextDays}
                                {' '}updates the leave type policy for future calculations. Existing {currentYear}
                                {' '}leave entitlements are not changed by this save.
                            </p>
                            <p style={{ margin: '10px 0 0', lineHeight: 1.6 }}>
                                {daysChangeConfirm.impactLoading && 'Checking entitlement impact...'}
                                {!daysChangeConfirm.impactLoading && daysChangeConfirm.impactError}
                                {!daysChangeConfirm.impactLoading
                                    && !daysChangeConfirm.impactError
                                    && Number.isFinite(daysChangeConfirm.impactedEntitlements)
                                    && `${daysChangeConfirm.impactedEntitlements} current-year entitlement${daysChangeConfirm.impactedEntitlements === 1 ? '' : 's'} may need a separate repair or generation review.`}
                            </p>
                            {loading && (
                                <p style={{ margin: '10px 0 0', lineHeight: 1.6, fontWeight: 600 }}>
                                    Updating leave type policy...
                                </p>
                            )}
                            <div style={{ marginTop: 16 }}>
                                <p style={{ margin: '0 0 6px', fontWeight: 600 }}>Eligibility rules:</p>
                                <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.6 }}>
                                    {eligibilityRules.map((rule) => (
                                        <li key={rule}>{rule}</li>
                                    ))}
                                </ul>
                            </div>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer
                    showDefaultClose={false}
                    buttons={[
                        {
                            ...BUTTON_PRESETS.CANCEL,
                            key: 'cancel-days-change',
                            label: 'Cancel',
                            disabled: loading,
                            onClick: () => setDaysChangeConfirm(null)
                        },
                        {
                            ...BUTTON_PRESETS.SAVE,
                            key: 'confirm-days-change',
                            label: 'Update Policy',
                            loading,
                            disabled: loading,
                            onClick: handleConfirmDaysChange
                        }
                    ]}
                />
            </Modal>
        </>
    );
};

export default LeaveTypeFormModal;
